import React, { MutableRefObject } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-bootstrap';
import { get, isEqual } from 'lodash';
import Keyboard, { KeyboardReactInterface } from 'react-simple-keyboard';
import inputMask from 'simple-keyboard-input-mask';

import { trims } from 'utils';
import { RootState } from 'rootReducer';
import { KioskState } from 'modules/kiosk/containers/Kiosk/services/reducers';
import { tripDetailsType } from 'entities/Trip';

import 'react-simple-keyboard/build/css/index.css';
import { getTripFieldsRequest } from 'modules/common/FillTrip';
import { VerifyCodeSelector } from 'modules/common/VerifyPhoneBtn/selectors';
import { VerifyPhoneState } from 'modules/common/VerifyPhoneBtn/reducer';
import { changeActiveInput } from 'modules/kiosk/containers/Kiosk/services/actions';
import { clearStateVerify, isErrorVerify, isSucessVerify } from 'modules/common/VerifyPhoneBtn/actions';
import { getDriverAndTransportDataRequest } from 'modules/security/containers/TripContainer/services/actions';
import { PhoneService } from '../../../../entities/Phone/service';

const layout = {
  default: [
    '1 2 3 4 5 6 7 8 9 0',
    'Q W E R T Y U I O P',
    'A S D F G H J K L',
    'Z X C V B N M {bksp}',
    '{enter} {space}',
  ],
  numeric: ['1 2 3', '4 5 6', '7 8 9', '0 {bksp}', '{enter} {space}'],
};

type Props = {
  onClose: () => void;
  onDeleteField: (type: tripDetailsType) => void;
  formValues: any;
  setFieldValue: any;
  keyboardRef: MutableRefObject<KeyboardReactInterface | null>;
  isKioskPhone?: boolean;
};

const CustomKeyboard: React.FC<Props> = ({
  formValues,
  setFieldValue,
  keyboardRef,
  isKioskPhone,
  onDeleteField,
  onClose,
}) => {
  const dispatch = useDispatch();
  const kioskReducer: KioskState = useSelector((state: RootState) => state.kioskModuleReducer.kioskReducer);
  const { activeInput } = kioskReducer;

  const type = activeInput.split('.')[0] as tripDetailsType;

  const isPhoneReq = isEqual(activeInput.split('.')[1], 'phone');
  const isWeight = activeInput === 'entryVehicleWeight';
  const islicensePlateReq = isEqual(activeInput.split('.')[1], 'licensePlate');

  const keyLayout = isPhoneReq || isWeight ? 'numeric' : 'default';

  const { code, isLoadingFields }: VerifyPhoneState = useSelector(VerifyCodeSelector);

  const onChangeInput = (e: any) => {
    if (isPhoneReq && e.length < 5) return;

    if (activeInput.includes('licensePlate')) {
      const val = get(formValues[type].document, 'number');

      if (val) {
        setFieldValue(`${type}.document.number`, '');
        setFieldValue(`${type}.document.pictures`, []);
      }
    }

    // @ts-ignore
    keyboardRef?.current?.setInput(e);
    return setFieldValue(activeInput, e);
  };

  const onInit = (keyboard: any) => {
    const value = get(formValues, activeInput);

    if (isPhoneReq && !isEqual(PhoneService.unformatPhone(value).slice(0, 3), '380')) {
      setFieldValue(activeInput, `38 (0${value}`);
      return keyboard.setInput(`38 (0${value}`);
    }

    keyboard.setInput(value);
  };

  const onAccept = () => {
    // @ts-ignore
    const value = keyboardRef?.current?.input[activeInput] || '';

    const isKeybVerify = isEqual(activeInput, 'keybVerifyCode');
    if (isPhoneReq) {
      onDeleteField(type);

      if (isKioskPhone) {
        dispatch(
          getDriverAndTransportDataRequest({
            withTransport: true,
            id: null,
            phoneNumber: PhoneService.unformatPhone(value),
          }),
        );
      } else {
        dispatch(
          getTripFieldsRequest({ fieldName: activeInput, paramName: 'phoneNumber', value, tripValue: formValues }),
        );
      }
    }

    if (islicensePlateReq) {
      onDeleteField(type);
      dispatch(
        getTripFieldsRequest({
          fieldName: activeInput, // @ts-ignore
          paramName: activeInput,
          value: trims(value),
          tripValue: formValues,
        }),
      );
    }

    if (isKeybVerify) {
      if (value === code) {
        dispatch(isSucessVerify(true));

        setFieldValue(isLoadingFields, 'true');
        setFieldValue('keybVerifyCode', '');

        return setTimeout(() => {
          dispatch(clearStateVerify());
          dispatch(changeActiveInput(''));
        }, 1500);
      }

      return dispatch(isErrorVerify(true));
    }

    onClose();
  };

  const onKeyPress = (btn: string) => {
    setTimeout(() => {
      try {
        if (!isPhoneReq) keyboardRef?.current?.setCaretPosition(null);

        if (btn === '{bksp}') {
          const input = get(formValues, activeInput);
          const lastChar = input[input.length - 1];

          if (lastChar === ')' || lastChar === ' ' || lastChar === '-') {
            setFieldValue(activeInput, input.slice(0, -2));
            return keyboardRef?.current?.setInput(input.slice(0, -2));
          }
        }

        switch (btn) {
          case '{enter}': {
            onClose();
            break;
          }
          case '{space}': {
            onAccept();
            break;
          }
          default:
            break;
        }
      } catch (err) {
        console.log(err);
      }
    }, 500);
  };

  return (
    <Form.Group onClick={(event: any) => event.stopPropagation()}>
      <Keyboard
        // eslint-disable-next-line
        keyboardRef={(r: any) => (keyboardRef.current = r)}
        inputName={activeInput}
        layoutName={keyLayout}
        onChange={onChangeInput}
        onKeyPress={onKeyPress}
        // debug
        beforeRender={(keyboard: any) => onInit(keyboard)}
        layout={layout}
        inputMask={{
          'driver.phone.number': {
            mask: '38 (099) 999-99-99',
            regex: /^[0-9]+$/,
          },
          'forwarder.phone.number': {
            mask: '38 (099) 999-99-99',
            regex: /^[0-9]+$/,
          },
          entryVehicleWeight: {
            mask: '999999',
            regex: /^[0-9]+$/,
          },
        }}
        maxLength={{
          'driver.phone.number': 12,
          'forwarder.phone.number': 12,
          entryVehicleWeight: 6,
          'vehicle.licensePlate': 10,
          'trailer.licensePlate': 10,
        }}
        modules={[inputMask]}
        display={{
          '{clear}': 'стерти',
          '{bksp}': '\u232B',
          '{enter}': '\u2716',
          '{space}': '\u2714',
        }}
      />
    </Form.Group>
  );
};

export default CustomKeyboard;
